import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, X, User, Building, Mail, Linkedin, Globe, FileText, Download, ExternalLink, ArrowRight } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import VideoHelpButton from '../components/VideoHelpButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to download files with authentication
const downloadFileWithAuth = async (url, filename) => {
  try {
    const response = await fetch(url, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Download failed');
    }
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Download error:', error);
    alert('Failed to download file');
  }
};

// Event Detail Modal Component
const EventDetailModal = ({ event, isOpen, onClose, onNavigateToCard, isDark }) => {
  if (!isOpen || !event) return null;

  return (
    <div className={`fixed inset-0 ${isDark ? 'bg-black/80' : 'bg-black/50'} flex items-center justify-center z-50 p-4`} onClick={onClose}>
      <div 
        className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-gray-50 border-gray-200'} border-b p-6 flex items-center justify-between flex-shrink-0`}>
          <div className="flex items-center gap-4">
            <CalendarIcon className={`w-8 h-8 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
            <div>
              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                {event.title}
              </h2>
              <p className="text-gray-400 text-sm">QSECDEF Event Details</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => onNavigateToCard(event)}
              className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-[#A2FF00]/20 text-[#A2FF00] hover:bg-[#A2FF00]/30' : 'bg-green-100 text-green-700 hover:bg-green-200'} text-sm font-bold rounded-sm transition-colors`}
              title="View in Member Tracker"
            >
              <span>View in Tracker</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'} transition-colors`}
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="p-6 overflow-y-auto flex-grow space-y-6">
          
          {/* Event Date & Status Banner */}
          <div className={`bg-gradient-to-r ${isDark ? 'from-[#A2FF00]/10' : 'from-green-50'} to-transparent border-l-4 border-[#A2FF00] p-4 rounded-sm`}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-gray-400 text-sm">Event Date</p>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} font-mono`}>
                  {event.event_date ? format(new Date(event.event_date), 'EEEE, MMMM dd, yyyy') : 'Date TBD'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-gray-400 text-sm">Asset Status</p>
                <span className={`inline-block text-sm px-3 py-1 rounded ${
                  event.asset_collection_status === 'submitted' 
                    ? 'bg-green-500/20 text-green-400'
                    : event.asset_collection_status === 'overdue'
                    ? 'bg-red-500/20 text-red-400'
                    : 'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {event.asset_collection_status || 'Pending'}
                </span>
              </div>
            </div>
            {event.asset_deadline && (
              <p className="text-gray-500 text-sm mt-2">
                Asset Deadline: {format(new Date(event.asset_deadline), 'MMM dd, yyyy')}
              </p>
            )}
          </div>

          {/* Company Info Section */}
          <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-5`}>
            <h3 className={`text-lg font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'} mb-4 flex items-center gap-2`}>
              <Building className="w-5 h-5" />
              Company Information
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-gray-500 text-xs mb-1">Company Name</p>
                <p className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium`}>{event.title}</p>
              </div>
              {event.description && (
                <div className="md:col-span-2">
                  <p className="text-gray-500 text-xs mb-1">Description</p>
                  <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{event.description}</p>
                </div>
              )}
              {event.company_address && (
                <div>
                  <p className="text-gray-500 text-xs mb-1">Address</p>
                  <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{event.company_address}</p>
                </div>
              )}
              {(event.city_town || event.country) && (
                <div>
                  <p className="text-gray-500 text-xs mb-1">Location</p>
                  <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    {[event.city_town, event.country].filter(Boolean).join(', ')}
                  </p>
                </div>
              )}
              {event.website_url && (
                <div>
                  <p className="text-gray-500 text-xs mb-1">Website</p>
                  <a 
                    href={event.website_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`${isDark ? 'text-[#A2FF00] hover:text-[#8EFF00]' : 'text-green-600 hover:text-green-700'} inline-flex items-center gap-1`}
                  >
                    <Globe className="w-4 h-4" />
                    {event.website_url}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
              {event.company_registration && (
                <div>
                  <p className="text-gray-500 text-xs mb-1">Company Registration</p>
                  <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} font-mono`}>{event.company_registration}</p>
                </div>
              )}
            </div>
          </div>

          {/* Public Company Description Section */}
          {event.customer_bio && (
            <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-5`}>
              <h3 className={`text-lg font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'} mb-4 flex items-center gap-2`}>
                <FileText className="w-5 h-5" />
                Public Company Description
              </h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} whitespace-pre-wrap`}>{event.customer_bio}</p>
            </div>
          )}

          {/* Customer Logo */}
          {event.customer_photo_url && (
            <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-5`}>
              <h3 className={`text-lg font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'} mb-4`}>Company Logo</h3>
              <div className="flex items-start gap-4">
                <img 
                  src={`${BACKEND_URL}${event.customer_photo_url}`}
                  alt="Customer"
                  className={`w-32 h-32 object-cover rounded-sm border ${isDark ? 'border-[#A2FF00]/20' : 'border-green-200'}`}
                />
                <button
                  onClick={() => downloadFileWithAuth(
                    `${BACKEND_URL}${event.customer_photo_url}`,
                    event.customer_photo_url.split('/').pop()
                  )}
                  className={`text-sm ${isDark ? 'text-[#A2FF00] hover:text-[#8EFF00]' : 'text-green-600 hover:text-green-700'} inline-flex items-center gap-1`}
                >
                  <Download className="w-4 h-4" />
                  Download Photo
                </button>
              </div>
            </div>
          )}

          {/* Speakers Section */}
          {event.speakers && event.speakers.length > 0 && event.speakers.some(s => s && s.name) && (
            <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-5`}>
              <h3 className={`text-lg font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'} mb-4 flex items-center gap-2`}>
                <User className="w-5 h-5" />
                Event Speakers
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                {event.speakers.map((speaker, index) => speaker && speaker.name && (
                  <div key={index} className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-4`}>
                    <div className="flex items-start gap-4">
                      {speaker.photo_url ? (
                        <img 
                          src={`${BACKEND_URL}${speaker.photo_url}`}
                          alt={speaker.name}
                          className={`w-20 h-20 object-cover rounded-sm border ${isDark ? 'border-[#A2FF00]/20' : 'border-green-200'}`}
                        />
                      ) : (
                        <div className={`w-20 h-20 ${isDark ? 'bg-[#111] border-white/10' : 'bg-gray-100 border-gray-200'} border rounded-sm flex items-center justify-center`}>
                          <User className="w-8 h-8 text-gray-500" />
                        </div>
                      )}
                      <div className="flex-1">
                        <h4 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{speaker.name}</h4>
                        {speaker.title && (
                          <p className={`${isDark ? 'text-[#A2FF00]' : 'text-green-600'} text-sm`}>{speaker.title}</p>
                        )}
                        {speaker.email && (
                          <a 
                            href={`mailto:${speaker.email}`}
                            className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'} text-sm inline-flex items-center gap-1 mt-1`}
                          >
                            <Mail className="w-3 h-3" />
                            {speaker.email}
                          </a>
                        )}
                        {speaker.linkedin_url && (
                          <a 
                            href={speaker.linkedin_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-400 text-sm hover:text-[#0A66C2] inline-flex items-center gap-1 mt-1 ml-3"
                          >
                            <Linkedin className="w-3 h-3" />
                            LinkedIn
                          </a>
                        )}
                      </div>
                    </div>
                    {speaker.bio && (
                      <p className={`${isDark ? 'text-gray-400 border-white/10' : 'text-gray-500 border-gray-200'} text-sm mt-3 border-t pt-3`}>
                        {speaker.bio}
                      </p>
                    )}
                    {speaker.photo_url && (
                      <button
                        onClick={() => downloadFileWithAuth(
                          `${BACKEND_URL}${speaker.photo_url}`,
                          speaker.photo_url.split('/').pop()
                        )}
                        className={`text-xs ${isDark ? 'text-[#A2FF00] hover:text-[#8EFF00]' : 'text-green-600 hover:text-green-700'} inline-flex items-center gap-1 mt-2`}
                      >
                        <Download className="w-3 h-3" />
                        Download Photo
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Key Contacts Section */}
          {event.key_people && event.key_people.length > 0 && event.key_people.some(p => p && p.name) && (
            <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-5`}>
              <h3 className={`text-lg font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'} mb-4 flex items-center gap-2`}>
                <User className="w-5 h-5" />
                Key Contacts
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {event.key_people.map((person, index) => person && person.name && (
                  <div 
                    key={index} 
                    className={`${isDark ? 'bg-[#0A0A0A]' : 'bg-white'} border rounded-sm p-3 ${
                      person.role === 'main_contact' ? (isDark ? 'border-[#A2FF00]/30' : 'border-green-300') : (isDark ? 'border-white/10' : 'border-gray-200')
                    }`}
                  >
                    <p className={`text-xs font-medium mb-1 ${
                      person.role === 'main_contact' ? (isDark ? 'text-[#A2FF00]' : 'text-green-600') : 'text-gray-500'
                    }`}>
                      {person.role === 'main_contact' ? 'Main Contact' :
                       person.role === 'billing' ? 'Billing Contact' :
                       person.role === 'cmo' ? 'CMO' :
                       person.role === 'cro' ? 'CRO' :
                       person.role === 'ceo' ? 'CEO' : 'Contact'}
                    </p>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {person.name}
                    </p>
                    {person.title && (
                      <p className="text-gray-400 text-sm">{person.title}</p>
                    )}
                    {person.email && (
                      <a href={`mailto:${person.email}`} className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'} text-sm block`}>
                        {person.email}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Contract Info */}
          {(event.billing_amount_gbp || event.relationship_status) && (
            <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-5`}>
              <h3 className={`text-lg font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'} mb-4`}>Contract Information</h3>
              <div className="grid md:grid-cols-3 gap-4">
                {event.relationship_status && (
                  <div>
                    <p className="text-gray-500 text-xs mb-1">Relationship Status</p>
                    <span className={`inline-block px-2 py-1 rounded text-sm capitalize ${
                      event.relationship_status === 'customer' ? 'bg-green-500/20 text-green-400' :
                      event.relationship_status === 'churned' ? 'bg-red-500/20 text-red-400' :
                      event.relationship_status === 'renewing' ? 'bg-blue-500/20 text-blue-400' :
                      event.relationship_status === 'paused' ? 'bg-orange-500/20 text-orange-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {event.relationship_status}
                    </span>
                  </div>
                )}
                {event.billing_amount_gbp && (
                  <div>
                    <p className="text-gray-500 text-xs mb-1">Billing Amount</p>
                    <p className={`${isDark ? 'text-white' : 'text-gray-900'} font-mono text-lg`}>£{event.billing_amount_gbp.toLocaleString()}</p>
                    {event.billing_type && (
                      <p className="text-gray-400 text-sm">/ {event.billing_type}</p>
                    )}
                  </div>
                )}
                {event.contract_date && (
                  <div>
                    <p className="text-gray-500 text-xs mb-1">Contract Date</p>
                    <p className={`${isDark ? 'text-white' : 'text-gray-900'} font-mono`}>{format(new Date(event.contract_date), 'MMM dd, yyyy')}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-gray-50 border-gray-200'} border-t p-4 flex-shrink-0`}>
          <button
            onClick={onClose}
            className={`w-full px-6 py-2 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-green-500 hover:bg-green-600'} text-black font-bold rounded-sm transition-colors`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const EventsCalendar = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [activeTab, setActiveTab] = useState('upcoming');

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/events`, { credentials: 'include' });
      if (response.ok) {
        setEvents(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateToCard = (event) => {
    setSelectedEvent(null);
    navigate(`/board/${event.board_id}?card=${event.card_id}`);
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const upcomingEvents = events
    .filter(event => {
      if (!event.event_date) return true;
      const eventDate = new Date(event.event_date);
      eventDate.setHours(0, 0, 0, 0);
      return eventDate >= today;
    })
    .sort((a, b) => {
      if (!a.event_date) return 1;
      if (!b.event_date) return -1;
      return new Date(a.event_date) - new Date(b.event_date);
    });

  const pastEvents = events
    .filter(event => {
      if (!event.event_date) return false;
      const eventDate = new Date(event.event_date);
      eventDate.setHours(0, 0, 0, 0);
      return eventDate < today;
    })
    .sort((a, b) => {
      return new Date(b.event_date) - new Date(a.event_date);
    });

  const displayEvents = activeTab === 'upcoming' ? upcomingEvents : pastEvents;

  const getSpeakerNames = (event) => {
    if (event.speakers && event.speakers.length > 0) {
      return event.speakers.filter(s => s && s.name).map(s => s.name).join(', ');
    }
    return null;
  };

  if (loading) {
    return (
      <AppLayout title="Events Calendar">
        <div className="text-[#A2FF00]">Loading...</div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Events Calendar">
      {/* Tabs */}
      <div className={`flex items-center gap-4 mb-6 border-b ${isDark ? 'border-white/10' : 'border-gray-200'} pb-4`}>
        <button
          onClick={() => setActiveTab('upcoming')}
          className={`px-4 py-2 font-bold text-sm rounded-sm transition-colors ${
            activeTab === 'upcoming'
              ? `${isDark ? 'bg-[#A2FF00]' : 'bg-green-500'} text-black`
              : `bg-transparent ${isDark ? 'text-gray-400 hover:text-white border-white/20' : 'text-gray-600 hover:text-gray-900 border-gray-300'} border`
          }`}
        >
          Upcoming Events
          {upcomingEvents.length > 0 && (
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
              activeTab === 'upcoming' ? 'bg-black/20' : (isDark ? 'bg-[#A2FF00]/20 text-[#A2FF00]' : 'bg-green-100 text-green-700')
            }`}>
              {upcomingEvents.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('past')}
          className={`px-4 py-2 font-bold text-sm rounded-sm transition-colors ${
            activeTab === 'past'
              ? `${isDark ? 'bg-[#A2FF00]' : 'bg-green-500'} text-black`
              : `bg-transparent ${isDark ? 'text-gray-400 hover:text-white border-white/20' : 'text-gray-600 hover:text-gray-900 border-gray-300'} border`
          }`}
        >
          Past Events
          {pastEvents.length > 0 && (
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
              activeTab === 'past' ? 'bg-black/20' : 'bg-gray-500/20 text-gray-400'
            }`}>
              {pastEvents.length}
            </span>
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {displayEvents.map(event => (
          <div
            key={event.card_id}
            data-testid={`event-${event.card_id}`}
            onClick={() => setSelectedEvent(event)}
            className={`${isDark ? 'bg-[#0A0A0A]' : 'bg-white'} border rounded-sm p-6 hover:border-[#A2FF00]/50 transition-colors cursor-pointer group ${
              activeTab === 'past' ? (isDark ? 'border-white/5 opacity-80' : 'border-gray-100 opacity-80') : (isDark ? 'border-white/10' : 'border-gray-200')
            }`}
          >
            <div className="flex items-start justify-between mb-4">
              <CalendarIcon className={`w-8 h-8 ${activeTab === 'past' ? 'text-gray-500' : (isDark ? 'text-[#A2FF00]' : 'text-green-600')}`} />
              <span className={`text-xs px-2 py-1 rounded ${
                event.asset_collection_status === 'submitted' 
                  ? 'bg-green-500/20 text-green-400'
                  : event.asset_collection_status === 'overdue'
                  ? 'bg-red-500/20 text-red-400'
                  : 'bg-yellow-500/20 text-yellow-400'
              }`}>
                {event.asset_collection_status || 'pending'}
              </span>
            </div>
            <h3 className={`text-xl font-bold mb-2 ${isDark ? 'group-hover:text-[#A2FF00]' : 'group-hover:text-green-600'} transition-colors ${
              activeTab === 'past' ? (isDark ? 'text-gray-300' : 'text-gray-500') : (isDark ? 'text-white' : 'text-gray-900')
            }`}>
              {event.title}
            </h3>
            <div className="space-y-2 text-sm">
              <div className="text-gray-400">
                <span className="text-gray-500">Event Date:</span>{' '}
                <span className={`font-mono ${activeTab === 'past' ? 'text-gray-400' : (isDark ? 'text-white' : 'text-gray-900')}`}>
                  {event.event_date ? format(new Date(event.event_date), 'MMM dd, yyyy') : 'N/A'}
                </span>
              </div>
              {event.asset_deadline && activeTab === 'upcoming' && (
                <div className="text-gray-400">
                  <span className="text-gray-500">Asset Deadline:</span>{' '}
                  <span className={`${isDark ? 'text-white' : 'text-gray-900'} font-mono`}>
                    {format(new Date(event.asset_deadline), 'MMM dd, yyyy')}
                  </span>
                </div>
              )}
              {getSpeakerNames(event) && (
                <div className={`text-gray-400 pt-2 border-t ${isDark ? 'border-white/10' : 'border-gray-200'} mt-2`}>
                  <span className="text-gray-500">Speakers:</span>{' '}
                  <span className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    {getSpeakerNames(event)}
                  </span>
                </div>
              )}
            </div>
            <div className={`mt-4 pt-3 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <span className={`text-xs ${isDark ? 'text-[#A2FF00] group-hover:text-[#8EFF00]' : 'text-green-600 group-hover:text-green-700'}`}>
                Click to view details →
              </span>
            </div>
          </div>
        ))}
        {displayEvents.length === 0 && (
          <div className="col-span-full text-center text-gray-400 py-12">
            {activeTab === 'upcoming' ? 'No upcoming events.' : 'No past events.'}
          </div>
        )}
      </div>

      <EventDetailModal 
        event={selectedEvent}
        isOpen={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        onNavigateToCard={handleNavigateToCard}
        isDark={isDark}
      />
    </AppLayout>
  );
};

export default EventsCalendar;
